import { collection, doc, addDoc, getDoc, getDocs, updateDoc, query, where, serverTimestamp, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { COLLECTIONS } from "./collections";
import { cashierShiftConverter } from "../models/cashierShift/cashierShiftConverter";

class CashierShiftService {
    /**
     * Referência tipada para a subcoleção cashierShifts
     */
    getCollectionRef(idTenant) {
        if (!idTenant) throw new Error("idTenant é obrigatório.");
        return collection(db, "tenants", idTenant, COLLECTIONS.CASHIER_SHIFTS).withConverter(cashierShiftConverter);
    }

    /**
     * Busca o turno de caixa que está ABERTO para uma determinada conta/caixa
     */
    async getCurrentOpenShift(idTenant, idBankAccount = null) {
        const shiftsRef = this.getCollectionRef(idTenant);
        let q;
        if (idBankAccount) {
            q = query(
                shiftsRef,
                where("status", "==", "OPEN"),
                where("idBankAccount", "==", idBankAccount)
            );
        } else {
            q = query(
                shiftsRef,
                where("status", "==", "OPEN")
            );
        }

        const snapshot = await getDocs(q);
        if (snapshot.empty) {
            return null;
        }
        
        // Retorna o primeiro caixa aberto (geralmente só pode existir 1 por bankAccount)
        const shiftData = snapshot.docs[0].data();
        return shiftData;
    }

    /**
     * Lista todos os turnos de caixa fechados ou abertos registrados num dia específico
     */
    async getShiftsByDate(idTenant, dateString) {
        const shiftsRef = this.getCollectionRef(idTenant);
        const q = query(
            shiftsRef,
            where("dateString", "==", dateString),
            orderBy("openedAt", "desc")
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data());
    }

    /**
     * Abre um novo turno de caixa
     */
    async openShift(idTenant, { idBankAccount, operatorId, openingBalance }) {
        // Valida se já não tem um aberto pra essa mesma gaveta
        const currentOpen = await this.getCurrentOpenShift(idTenant, idBankAccount);
        if (currentOpen) {
            throw new Error("Já existe um caixa aberto para esta conta. Feche-o primeiro.");
        }

        const shiftsRef = this.getCollectionRef(idTenant);
        const todayStr = new Date().toISOString().split("T")[0];

        const newShift = {
            idBankAccount,
            operatorId,
            status: "OPEN",
            dateString: todayStr,
            openedAt: serverTimestamp(),
            openingBalance: openingBalance || 0,
            closingBalance: null,
            systemComputedBalance: null,
            difference: null,
        };

        const docRef = await addDoc(shiftsRef, newShift);
        const createdDoc = await getDoc(docRef);
        return createdDoc.data();
    }

    /**
     * Fechamento cego de caixa
     */
    async closeShift(idTenant, shiftId, { closingBalance, systemComputedBalance }) {
        if (!idTenant || !shiftId) throw new Error("idTenant e shiftId são obrigatórios.");

        const finalClosingBalance = Number(closingBalance) || 0;
        const finalComputedBalance = Number(systemComputedBalance) || 0;
        const difference = finalClosingBalance - finalComputedBalance;
        
        // Remove withConverter para updateDoc pois é apenas uma alteração parcial
        const docRef = doc(db, "tenants", idTenant, COLLECTIONS.CASHIER_SHIFTS, shiftId);
        
        await updateDoc(docRef, {
            status: "CLOSED",
            closedAt: serverTimestamp(),
            closingBalance: finalClosingBalance,
            systemComputedBalance: finalComputedBalance,
            difference,
            updatedAt: serverTimestamp()
        });

        // Usamos doc() novamente do zero com o converter apensado, em vez de atrelar a um let/const já inicializado, pois comV9 isso pode espirrar
        const typedDocRef = doc(db, "tenants", idTenant, COLLECTIONS.CASHIER_SHIFTS, shiftId).withConverter(cashierShiftConverter);
        const updatedDoc = await getDoc(typedDocRef);
        return updatedDoc.data();
    }
}

const cashierShiftService = new CashierShiftService();
export default cashierShiftService;
